import React, { useCallback, useEffect, useState, useMemo } from 'react'

import { STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'

import { useStatsigExperiment } from './useStatsigExperiment'

/**
 * Generic component for Statsig experiments that only renders when needed
 */
interface StatsigExperimentProps {
  experimentKey: STATSIG_EXPERIMENTS
  onRender: (bucket: string | null, init: boolean) => void
}

const StatsigExperiment: React.FC<StatsigExperimentProps> = ({ experimentKey, onRender }) => {
  const { experimentBucket, init } = useStatsigExperiment(experimentKey)

  // Trigger onRender when the component mounts and unmounts.
  // The `init` flag indicates whether Statsig has completed initialization globally.
  // The experiment is marked as ready when both the component has mounted and Statsig has completed its initialization.
  useEffect(() => {
    onRender(experimentBucket, init)
    return () => {
      onRender(null, init)
    }
  }, [experimentBucket, onRender, init])

  return null
}

/**
 * Hook for conditionally rendering a Statsig experiment
 * @param experimentKey - The statsig experiment key
 * @param shouldRenderCondition - Function that determines if the experiment should be rendered
 * @returns An object containing the experiment bucket and component
 */
export function useConditionalStatsigExperiment(
  experimentKey: STATSIG_EXPERIMENTS,
  shouldRenderCondition: () => boolean,
): { bucket: string | null; component: React.ReactNode; isExperimentInitialized: boolean } {
  const [experimentBucket, setExperimentBucket] = useState<string | null>(null)
  const [isExperimentInitialized, setIsExperimentInitialized] = useState<boolean>(false)

  const handleExperimentResult = useCallback((bucket: string | null, isInitialized: boolean) => {
    setExperimentBucket(bucket)
    setIsExperimentInitialized(isInitialized)
  }, [])

  const shouldRender = useMemo(() => shouldRenderCondition(), [shouldRenderCondition])

  const experimentComponent = useMemo(() => {
    return shouldRender ? <StatsigExperiment experimentKey={experimentKey} onRender={handleExperimentResult} /> : null
  }, [shouldRender, experimentKey, handleExperimentResult])

  return { bucket: experimentBucket, component: experimentComponent, isExperimentInitialized }
}
